import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DashboardOutlined as DashboardIcon,
  AdminPanelSettingsOutlined as AdminIcon,
  SmartToyOutlined as ChatIcon,
  DescriptionOutlined as SummariesIcon,
  CalendarViewWeekOutlined as WeeklySummariesIcon,
  ManageAccountsOutlined as ProcessesIcon,
  FeedbackOutlined as FeedbackIcon,
  Menu as MenuIcon,
  PeopleOutlined as PeopleIcon,
  GridViewOutlined as PortfolioIcon,
  AutoFixHighOutlined as GenerateIcon,
  MonitorHeartOutlined as MonitoringIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);

  // Admin roles: superadmin, admin, anis-admin (have full access)
  const adminRoles = ['admin', 'anis-admin', 'superadmin'];
  const isAdmin = user?.role ? adminRoles.includes(user.role) : false;

  // All authenticated users can access Getting Started
  const canAccessGettingStarted = !!user;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if user is on getting-started page or customer profiling
  const isOnGettingStarted = location.pathname === '/getting-started';
  const isOnCustomerProfiling = location.pathname.startsWith('/cp');

  // Show only "Getting Started" on getting-started page, all others inside ANIS
  const menuItems = [
    {
      text: 'Getting Started',
      icon: <MenuIcon />,
      path: '/getting-started',
      show: canAccessGettingStarted && isOnGettingStarted,
    },
    {
      text: 'Portfolio',
      icon: <PortfolioIcon />,
      path: '/cp',
      show: isOnCustomerProfiling,
    },
    {
      text: 'Generate',
      icon: <GenerateIcon />,
      path: '/cp/generate',
      show: isOnCustomerProfiling,
    },
    {
      text: 'User Management',
      icon: <PeopleIcon />,
      path: '/getting-started#user-management',
      show: isAdmin && isOnGettingStarted,
    },
    {
      text: isAdmin ? 'Dashboard' : 'User Dashboard',
      icon: <DashboardIcon />,
      path: '/anis/dashboard',
      show: !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Summaries',
      icon: <SummariesIcon />,
      path: '/anis/summaries',
      show: isAdmin && !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Weekly Summaries',
      icon: <WeeklySummariesIcon />,
      path: '/anis/weekly-summaries',
      show: isAdmin && !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Processes',
      icon: <ProcessesIcon />,
      path: '/anis/processes',
      show: isAdmin && !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Chatbot',
      icon: <ChatIcon />,
      path: '/anis/chatbot',
      show: !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Summaries',
      icon: <SummariesIcon />,
      path: '/anis/summaries',
      show: !isAdmin && !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Feedback',
      icon: <FeedbackIcon />,
      path: '/anis/feedback',
      show: !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'Admin Panel',
      icon: <AdminIcon />,
      path: '/admin',
      show: isAdmin && !isOnGettingStarted && !isOnCustomerProfiling,
    },
    {
      text: 'System Monitoring',
      icon: <MonitoringIcon />,
      path: '/admin/monitoring',
      show: isAdmin && !isOnGettingStarted && !isOnCustomerProfiling,
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (isMobile && open) {
      onToggle();
    }
  };

  // Check if current path matches menu item path
  const isCurrentPage = (path: string) => {
    // Handle hash-based paths
    if (path.includes('#')) {
      const [pathPart, hashPart] = path.split('#');
      return location.pathname === pathPart && location.hash === `#${hashPart}`;
    }
    // For non-hash paths, ensure there's no hash in the current location
    return location.pathname === path && !location.hash;
  };

  return (
    <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={isMobile ? onToggle : undefined}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          width: isMobile ? DRAWER_WIDTH : (open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED),
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? DRAWER_WIDTH : (open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED),
            boxSizing: 'border-box',
            backgroundColor: 'white',
            borderRight: '1px solid #e0e0e0',
            borderRadius: 0,
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
            display: 'flex',
            top: isMobile ? 0 : 64,
            height: isMobile ? '100vh' : 'calc(100vh - 64px)',
            flexDirection: 'column',
            position: isMobile ? 'fixed' : 'fixed',
            left: 0,
            zIndex: isMobile ? theme.zIndex.drawer : 'auto',
          },
        }}
      >


      {/* Toggle Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-end' : 'center',
          px: 1,
          py: 1,
        }}
      >
        <IconButton
          onClick={onToggle}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <img 
            src="/assets/sidebar.svg" 
            alt="Toggle Sidebar" 
            style={{ width: 20, height: 20 }} 
          />
        </IconButton>
      </Box>

      <Divider sx={{ mx: 1 }} />

      {/* Menu Items */}
      <List sx={{ px: 1, py: 1, flex: 1, overflow: 'auto' }}>
        {menuItems
          .filter((item) => item.show)
          .map((item, index) => (
            <ListItem 
              key={item.text} 
              disablePadding 
              sx={{ 
                mb: 0.5,
                ...(index === 0 && isMobile && { mt: 2 })
              }}
            >
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isCurrentPage(item.path)}
                sx={{
                  minHeight: 44,
                  borderRadius: 1,
                  px: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(163, 24, 48, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(163, 24, 48, 0.12)',
                    '& .MuiListItemIcon-root': {
                      color: '#a31830',
                    },
                    '& .MuiListItemText-primary': {
                      color: '#a31830',
                      fontWeight: 500,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    color: '#333',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.25rem',
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        color: '#333',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
      </List>


    </Drawer>
  );
};

export default Sidebar;